import cn from 'classnames';
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import { ReactNode } from 'react';
import router, { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import Breadcrumbs from '@components/global/Breadcrumbs';
import Container from '@components/global/Container';
import Layout from '@components/global/Layout';
import AlertInlineMessage from '@components/ui/AlertInlineMessage';
import Heading from '@components/ui/Heading';
import { MEC_TITLE } from '@utils/constants';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import ReturnsForm from '@components/returns/ReturnsForm';
import {
  FEATURE_FLAG_PROMO_BAR_SERVER_SIDE,
  FEATURE_FLAG_RETURNS,
} from '@config/feature-flag';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import styles from './order-lookup.module.scss';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

interface OrderLookupPageProps {
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[] | null;
  xDefault: string;
}

interface MECStores {
  stores: MyStoreData[] | null;
}

export async function getStaticPaths() {
  return {
    paths: [{ params: { locale: 'en' } }, { params: { locale: 'fr' } }],
    fallback: false,
  };
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<
  OrderLookupPageProps | MECStores
> | null> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  if (!locale) {
    throw new Error('No locale specified for page');
  }
  if (!FEATURE_FLAG_RETURNS) {
    return {
      notFound: true,
    };
  }

  const previewRef = getPreviewRef(preview, previewData);

  const [
    stores,
    promoMessages,
    serverSideTranslation,
    promoBarData,
    bannerStripData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    serverSideTranslations(locale, ['common', 'returns']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const canonicalUrl = localePathAbs(locale, `/returns/order-lookup`) ?? '';
  const langAltUrl = localeAltPathAbs(locale, `/returns/order-lookup`) ?? '';
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  return {
    props: {
      stores,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      promoBarData,
      bannerStripData,
    },
  };
}

export default function OrderLookup({
  canonicalUrl,
  langAltUrl,
  xDefault,
}: OrderLookupPageProps): ReactNode {
  const { t } = useTranslation('returns');
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';

  const description = t('returns.flow.seo.description');
  const title = t('returns.flow.orderLookup.seo.title');

  const handleOrderSuccess = (orderId: string) => {
    if (typeof window !== 'undefined') {
      window.location.href = `/${locale}/my-account/orders/${orderId}`;
      return;
    }
    router.push(`/${locale}/my-account/orders/${orderId}`);
  };

  return (
    <>
      <NextSeo
        noindex
        description={description}
        title={`${title} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title,
          description,
          locale,
        }}
      />

      <Container>
        <Breadcrumbs name={'Request return'} breadcrumbs={[]} />
        <div className={styles.pageContainer}>
          <div className={styles.leftCol}>
            <Heading
              styleLevel="2"
              semanticLevel="h1"
              className={styles.heading}
            >
              {t('returns.orderLookup.heading')}
            </Heading>
            <p>{t('returns.orderLookup.intro')}</p>
            <p>{t('returns.orderLookup.intro2')}</p>

            <ReturnsForm onSuccess={handleOrderSuccess} />
          </div>
          <div className={cn(styles.rightCol, styles.instructionsContainer)}>
            <Heading
              styleLevel="2"
              semanticLevel="h2"
              className={styles.heading}
            >
              {t('returns.orderLookup.instructions.heading')}
            </Heading>
            <p>{t('returns.orderLookup.instructions.intro')}</p>
            <hr className={styles.hr} />
            <ol className={styles.instructionsList}>
              <li>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('returns.orderLookup.instructions.step1'),
                  }}
                />
              </li>
              <li>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('returns.orderLookup.instructions.step2'),
                  }}
                />
              </li>
              <li>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('returns.orderLookup.instructions.step3'),
                  }}
                />
              </li>
              <li>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('returns.orderLookup.instructions.step4'),
                  }}
                />
              </li>
              <li>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('returns.orderLookup.instructions.step5'),
                  }}
                />
              </li>
              <li>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('returns.orderLookup.instructions.step6'),
                  }}
                />
              </li>
            </ol>
            <AlertInlineMessage
              messageType="INFORMATION"
              classNames={styles.alertMessage}
            >
              {t('returns.orderLookup.instructions.info')}
            </AlertInlineMessage>
            <p className={styles.fulfilledByMEC}>
              {t('returns.orderLookup.instructions.fulfilledByMEC')}{' '}
              <a
                href={`/${locale}/explore/returns-and-guarantee`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('returns.orderLookup.instructions.fulfilledByMECLinkText')}
              </a>
            </p>
          </div>
        </div>
      </Container>
    </>
  );
}

OrderLookup.Layout = Layout;
